import React, { createContext, useContext } from 'react';
import {
    CompoundInterestParams,
    Currency,
    DepositFrequency,
    ScenarioType,
    TaxRate,
} from '../../../../utils/compound-interest/types';

interface ScenarioTabItem {
    id: ScenarioType;
    interestRate: number;
    inflationRate: number;
}

interface SliderRange {
    min: number;
    max: number;
    step: number;
}

interface SliderRanges {
    initialDeposit: SliderRange;
    regularDeposit: SliderRange;
    interestRate: SliderRange;
    duration: SliderRange;
    inflationRate: SliderRange;
}

export interface CompoundInterestCalculatorContextValue {
    params: CompoundInterestParams;
    activeScenario: ScenarioType;
    allTabs: ScenarioTabItem[];
    scenarioIcons: Record<ScenarioType, string>;
    scenarioLabels: Record<ScenarioType, string>;
    sliderRanges: SliderRanges;
    initialDepositSliderMax: number;
    regularDepositSliderMax: number;
    isTaxExemptionByTestEnabled: boolean;
    handleChange: <K extends keyof CompoundInterestParams>(field: K, value: CompoundInterestParams[K]) => void;
    handleNumberChange: (field: keyof CompoundInterestParams, value: string) => void;
    handleScenarioChange: (scenarioId: ScenarioType) => void;
    handleCurrencyChange: (currency: Currency) => void;
    handleTaxExemptionByTestChange: () => void;
    handleDepositFrequencyChange: (frequency: DepositFrequency) => void;
    handleTaxRateChange: (taxRate: TaxRate) => void;
}

const CompoundInterestCalculatorContext = createContext<CompoundInterestCalculatorContextValue | null>(null);

interface CompoundInterestCalculatorProviderProps {
    value: CompoundInterestCalculatorContextValue;
    children: React.ReactNode;
}

export const CompoundInterestCalculatorProvider: React.FC<CompoundInterestCalculatorProviderProps> = ({
    value,
    children,
}) => {
    return (
        <CompoundInterestCalculatorContext.Provider value={value}>
            {children}
        </CompoundInterestCalculatorContext.Provider>
    );
};

export const useCompoundInterestCalculatorContext = () => {
    const context = useContext(CompoundInterestCalculatorContext);

    if (!context) {
        throw new Error('useCompoundInterestCalculatorContext must be used within CompoundInterestCalculatorProvider');
    }

    return context;
};
